import { useState, useEffect } from "react";
import axios from "axios";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import Landing from "@/pages/Landing";
import Auth from "@/pages/Auth";
import Dashboard from "@/pages/Dashboard";
import Chat from "@/pages/Chat";
import Documents from "@/pages/Documents";
import DocumentComparison from "@/pages/DocumentComparison";
import Search from "@/pages/Search";
import CabinetManagement from "@/pages/CabinetManagement";
import ContractDrafting from "@/pages/ContractDrafting";
import LegalWatch from "@/pages/LegalWatch";
import SettingsBilling from "@/pages/SettingsBilling";
import Workflows from "@/pages/Workflows";
import Knowledge from "@/pages/Knowledge";
import BulkProcessing from "@/pages/BulkProcessing";
import WorkflowBuilder from "@/pages/WorkflowBuilder";
import Todos from "@/pages/Todos";
import Translation from "@/pages/Translation";
import Onboarding from "@/pages/Onboarding";
import Dossiers from "@/pages/Dossiers";
import DossierDetail from "@/pages/DossierDetail";
import Clients from "@/pages/Clients";
import Facturation from "@/pages/Facturation";
import SuperAdmin from "@/pages/SuperAdmin";
import { Toaster } from "@/components/ui/sonner";
import { TimerProvider } from '@/contexts/TimerContext';
import ErrorBoundary from "@/components/ErrorBoundary";

// P0.5 — Set axios global defaults: send httpOnly cookie on every request
axios.defaults.withCredentials = true;

function App() {
  const [user, setUser] = useState(null);
  const [cabinet, setCabinet] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if user is logged in
    const token = localStorage.getItem('lawliet_token');
    const userData = localStorage.getItem('lawliet_user');
    const cabinetData = localStorage.getItem('lawliet_cabinet');
    
    if (token && userData) {
      setUser(JSON.parse(userData));
      if (cabinetData) {
        setCabinet(JSON.parse(cabinetData));
      }
    }
    setLoading(false);
  }, []);

  const handleLogin = (token, userData, cabinetName) => {
    localStorage.setItem('lawliet_token', token);
    localStorage.setItem('lawliet_user', JSON.stringify(userData));
    localStorage.setItem('lawliet_cabinet', JSON.stringify({ name: cabinetName }));
    setUser(userData);
    setCabinet({ name: cabinetName });
  };
  const handleOnboardingComplete = () => {
    const stored = JSON.parse(localStorage.getItem('lawliet_user') || '{}');
    const updated = { ...stored, onboarding_completed: true };
    localStorage.setItem('lawliet_user', JSON.stringify(updated));
    setUser(updated);
  };
  const handleLogout = () => {
    localStorage.removeItem('lawliet_token');
    localStorage.removeItem('lawliet_user');
    localStorage.removeItem('lawliet_cabinet');
    setUser(null);
    setCabinet(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-900"></div>
      </div>
    );
  }

  return (
    <div className="App">
      <ErrorBoundary>
      <TimerProvider>
        <Toaster position="top-right" />
        <BrowserRouter>
        <Routes>
          {/* Public Landing Page */}
          <Route path="/" element={<Landing />} />
          
          <Route
            path="/auth"
            element={
              !user
                ? <Auth onLogin={handleLogin} />
                : user.role === "super_admin"
                  ? <Navigate to="/superadmin" />
                  : user.onboarding_completed === false
                    ? <Navigate to="/onboarding" />
                    : <Navigate to="/dashboard" />
            }
          />
          <Route
            path="/onboarding"
            element={
              user
                ? user.onboarding_completed
                  ? <Navigate to="/dashboard" />
                  : <Onboarding user={user} onComplete={handleOnboardingComplete} />
                : <Navigate to="/auth" />
            }
          />
          <Route
            path="/dashboard"
            element={
              user
                ? user.onboarding_completed === false
                  ? <Navigate to="/onboarding" />
                  : <Dashboard user={user} cabinet={cabinet} onLogout={handleLogout} />
                : <Navigate to="/auth" />
            }
          />
          <Route
            path="/chat"
            element={user ? <Chat user={user} cabinet={cabinet} onLogout={handleLogout} /> : <Navigate to="/auth" />}
          />
          <Route
            path="/chat/:conversationId"
            element={user ? <Chat user={user} cabinet={cabinet} onLogout={handleLogout} /> : <Navigate to="/auth" />}
          />
          <Route
            path="/documents"
            element={user ? <Documents user={user} cabinet={cabinet} onLogout={handleLogout} /> : <Navigate to="/auth" />}
          />
          <Route
            path="/document-comparison"
            element={user ? <DocumentComparison user={user} cabinet={cabinet} onLogout={handleLogout} /> : <Navigate to="/auth" />}
          />
          <Route
            path="/search"
            element={user ? <Search user={user} cabinet={cabinet} onLogout={handleLogout} /> : <Navigate to="/auth" />}
          />
          <Route
            path="/cabinet"
            element={user ? <CabinetManagement user={user} cabinet={cabinet} onLogout={handleLogout} /> : <Navigate to="/auth" />}
          />
          <Route
            path="/contracts"
            element={user ? <ContractDrafting user={user} cabinet={cabinet} onLogout={handleLogout} /> : <Navigate to="/auth" />}
          />
          <Route
            path="/legal-watch"
            element={user ? <LegalWatch user={user} cabinet={cabinet} onLogout={handleLogout} /> : <Navigate to="/auth" />}
          />
          <Route
            path="/workflows"
            element={user ? <Workflows user={user} cabinet={cabinet} onLogout={handleLogout} /> : <Navigate to="/auth" />}
          />
          <Route
            path="/knowledge"
            element={user ? <Knowledge user={user} cabinet={cabinet} onLogout={handleLogout} /> : <Navigate to="/auth" />}
          />
          <Route
            path="/bulk-processing"
            element={user ? <BulkProcessing user={user} cabinet={cabinet} onLogout={handleLogout} /> : <Navigate to="/auth" />}
          />
          <Route
            path="/workflow-builder"
            element={user ? <WorkflowBuilder user={user} cabinet={cabinet} onLogout={handleLogout} /> : <Navigate to="/auth" />}
          />
          <Route
            path="/settings"
            element={user ? <SettingsBilling user={user} cabinet={cabinet} onLogout={handleLogout} /> : <Navigate to="/auth" />}
          />
          <Route
            path="/todos"
            element={user ? <Todos user={user} cabinet={cabinet} onLogout={handleLogout} /> : <Navigate to="/auth" />}
          />
          <Route
            path="/translation"
            element={user ? <Translation user={user} cabinet={cabinet} onLogout={handleLogout} /> : <Navigate to="/auth" />}
          />
          <Route
            path="/dossiers"
            element={user ? <Dossiers user={user} cabinet={cabinet} onLogout={handleLogout} /> : <Navigate to="/auth" />}
          />
          <Route
            path="/dossiers/:id"
            element={user ? <DossierDetail user={user} cabinet={cabinet} onLogout={handleLogout} /> : <Navigate to="/auth" />}
          />
          <Route
            path="/clients"
            element={user ? <Clients user={user} cabinet={cabinet} onLogout={handleLogout} /> : <Navigate to="/auth" />}
          />
          <Route
            path="/facturation"
            element={user ? <Facturation user={user} cabinet={cabinet} onLogout={handleLogout} /> : <Navigate to="/auth" />}
          />
          {/* Super Admin — plateforme uniquement */}
          <Route
            path="/superadmin"
            element={
              user
                ? user.role === "super_admin"
                  ? <SuperAdmin user={user} onLogout={handleLogout} />
                  : <Navigate to="/dashboard" />
                : <Navigate to="/auth" />
            }
          />
          {/* Catch-all: unknown URLs go home instead of a blank screen */}
          <Route path="*" element={<Navigate to={user ? "/dashboard" : "/"} replace />} />
        </Routes>
        </BrowserRouter>
      </TimerProvider>
      </ErrorBoundary>
    </div>
  );
}

export default App;
